import { Builder } from '@builder.io/react'
import CdnImage from 'components/util/CdnImage'
import React from 'react'

enum Alignment {
  Left = 'leftAlign',
  Center = 'centerAlign',
  Right = 'rightAlign'
}

enum Rounding {
  none = 'none',
  xl = 'xl',
  twoXL = '2xl',
  threeXL = '3xl'
}

enum Device {
  desktop = 'desktop',
  mobile = 'mobile',
  desktopAndMobile = 'desktopAndMobile'
}

interface Props {
  imageUrl: string
  imageAltText: string
  imageFit: string
  imageAlignment: Alignment
  imageBlockDisplayStyle: boolean
  containerMaxWidth: boolean
  imageMaxWidth: string
  imageMaxWidthMobile: string
  autoWidth: boolean
  imageMaxHeight: string
  imageMaxHeightMobile: string
  autoHeight: boolean
  aspectRatio: string
  linkUrl: string
  linkNewPage: boolean
  steps: string
  roundedCorners: Rounding
  bannerEnabled: boolean
  bannerText: string
  bannerColor: string
  showOnDevice: Device
}

const ResponsiveImage = ({
  imageUrl,
  imageAltText,
  imageFit,
  imageAlignment,
  imageBlockDisplayStyle,
  containerMaxWidth,
  imageMaxWidth,
  imageMaxWidthMobile,
  autoWidth,
  imageMaxHeight,
  imageMaxHeightMobile,
  autoHeight,
  aspectRatio,
  linkUrl,
  linkNewPage,
  steps,
  roundedCorners,
  bannerEnabled = false,
  bannerText,
  bannerColor,
  showOnDevice = Device.desktopAndMobile
}: Props) => {
  const getAlignmentClass = () => {
    if (imageAlignment === Alignment.Right) {
      return 'justify-end'
    }
    if (imageAlignment === Alignment.Left) {
      return 'justify-start'
    }
    return 'justify-center mx-auto' // Center
  }

  const getRoundingClass = () => {
    if (roundedCorners === Rounding.xl) {
      return 'rounded-xl'
    }
    if (roundedCorners === Rounding.twoXL) {
      return 'rounded-2xl'
    }
    if (roundedCorners === Rounding.threeXL) {
      return 'rounded-3xl'
    }
    return 'rounded-none' // No rounding
  }

  const getDisplayDeviceClass = () => {
    if (showOnDevice === Device.desktop) {
      return 'hidden lg:block'
    }
    if (showOnDevice === Device.mobile) {
      return 'block lg:hidden'
    }

    return '' // Display on both desktop and mobile
  }

  const getImageWidth = () => {
    const addPx = value => (typeof value === 'string' && value.endsWith('px') ? value : `${value}px`)

    if (autoWidth) return 'auto'

    if (imageMaxWidth || imageMaxWidthMobile) {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 700
      const selectedWidth = isMobile ? imageMaxWidthMobile || imageMaxWidth : imageMaxWidth || imageMaxWidthMobile
      return selectedWidth ? addPx(selectedWidth) : '100%'
    }

    return '100%'
  }

  const getImageHeight = () => {
    const addPx = value => (typeof value === 'string' && value.endsWith('px') ? value : `${value}px`)

    if (autoHeight) return 'auto'

    if (imageMaxHeight || imageMaxHeightMobile) {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 700
      const selectedHeight = isMobile ? imageMaxHeightMobile || imageMaxHeight : imageMaxHeight || imageMaxHeightMobile
      return selectedHeight ? addPx(selectedHeight) : '100%'
    }

    return '100%'
  }

  const renderImage = () => {
    let stepValues: any = []
    if (steps) stepValues = steps?.split(',').map(Number) || []

    return (
      <div
        className={`relative ${getDisplayDeviceClass()} ${imageBlockDisplayStyle ? 'block' : 'flex'} ${getAlignmentClass()} ${containerMaxWidth ? 'w-3/4' : 'w-full'} `}
      >
        <CdnImage
          url={imageUrl}
          alt={imageAltText}
          steps={stepValues.length > 0 ? stepValues : undefined}
          className={`relative ${getRoundingClass()}`}
          // renderPlaceholder={false}
          style={{
            aspectRatio: aspectRatio,
            objectFit: imageFit,
            height: getImageHeight(),
            width: getImageWidth()
          }}
        />
        {bannerEnabled && (
          <div
            className={`absolute bottom-0 left-0 w-full p-4 text-center rounded-t-none ${getRoundingClass()}`}
            style={{
              backgroundColor: bannerColor
            }}
          >
            {bannerText && <p dangerouslySetInnerHTML={{ __html: bannerText }}></p>}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {' '}
      {!linkUrl && <>{renderImage()}</>}
      {linkUrl && (
        <>
          <a target={linkNewPage ? '_blank' : '_self'} href={`${linkUrl}`} rel={linkNewPage ? 'noreferrer' : undefined}>
            {renderImage()}
          </a>
        </>
      )}
    </>
  )
}

export default ResponsiveImage

Builder.registerComponent(ResponsiveImage, {
  name: 'ResponsiveImage',
  inputs: [
    {
      name: 'imageUrl',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'imageFit',
      type: 'string',
      enum: ['cover', 'contain'],
      defaultValue: 'contain'
    },
    {
      name: 'imageAlignment',
      type: 'string',
      enum: ['leftAlign', 'centerAlign', 'rightAlign'],
      defaultValue: 'centerAlign'
    },
    {
      name: 'imageBlockDisplayStyle',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'containerMaxWidth',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'imageMaxWidth',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'imageMaxWidthMobile',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'autoWidth',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'imageMaxHeight',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'imageMaxHeightMobile',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'autoHeight',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'aspectRatio',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'linkUrl',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'linkNewPage',
      type: 'string',
      defaultValue: false
    },
    {
      name: 'steps',
      type: 'string',
      defaultValue: false
    },
    {
      name: 'roundedCorners',
      type: 'string',
      enum: ['none', 'xl', '2xl', '3xl'],
      defaultValue: 'xl'
    },
    {
      name: 'bannerEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'bannerText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'bannerColor',
      type: 'color',
      defaultValue: '#FFFFFF'
    },
    {
      name: 'showOnDevice',
      type: 'string',
      enum: ['desktop', 'mobile', 'desktopAndMobile'],
      defaultValue: 'desktopAndMobile'
    }
  ]
})
